import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { Messages } from '../../../gen-src/Localize';
import { encodeSvg } from '../../icons/Icons';
import { PreventUserSelect } from '../../styles/PreventUserSelect';
import { TextAreaProps } from '../TextAreaWidget';
import { TabStyles } from '../../styles/TabStyles';
import { makeTestId } from '../../utils/Declarative';
import { generateId } from '../../utils/DomUtil';
import { TextAreaAttrs } from '../../utils/HtmlAttrs';
import { TextAreaWidget } from '../TextAreaWidget';
import { InfoIcon, Tooltip } from '../Tooltip/Tooltip';
import { TextFormatterHeight } from './TextFormatterDisplayWidget';

export interface TextFormatterEditorProps extends Pick<TextAreaProps, 'valid' | 'message'> {
  disabled?: boolean;
  /** Supportive text to display in a tooltip for when the toolbar is disabled */
  disabledTooltipText?: string;
  /** Markup to display in the editor */
  markup: string;
  /**
   * Label to display above the field.
   * You must provide a label to be WCAG-compliant.
   */
  label: React.ReactNode;
  /** If supplied, will be applied to the root div, whereas oll other HTMLAttributes will be applied to the textarea */
  style?: React.CSSProperties;
  /** Adds suffix '-TextFormatterEditorWidget' */
  testId: string;
}

const TextAreaStyleName = TypeStyle.style({
  display: 'flex',
  $nest: {
    '> textarea': {
      fontFamily: 'monospace',
      height: TextFormatterHeight,
      // Safari defaults to a 2px left/right margin on textarea, which causes a jump when switching modes
      margin: 0,
      resize: 'vertical',
    },
  }
});

const MarkupDocsStyleName = TypeStyle.style({
  bottom: 0,
  display: 'inline-flex',
  fontSize: 12,
  position: 'absolute',
  right: 0,
});

const EditorStyleName = TypeStyle.style({
  position: 'relative',
});

export const TextFormatterEditorWidget: React.FC<TextFormatterEditorProps & TextAreaAttrs> = props => {
  const { className, disabled, disabledTooltipText, id, label, markup, message, onChange, style, testId, valid, ...restProps } = props;
  // const [activeFormatters, setActiveFormatters] = React.useState<Array<FormatterType>>([]);
  // const [focusWithin, setFocusWithin] = React.useState(false);
  const [textAreaId] = React.useState<string>(id || generateId());
  const textAreaRef = React.useRef<HTMLTextAreaElement>(null);

  const handleChange = React.useCallback<React.ChangeEventHandler<HTMLTextAreaElement>>(e => {
    if (e.target.value !== markup) {
      // setActiveFormatters(getActiveFormatters(textAreaRef.current));
    }
    onChange?.(e);
  }, [onChange, textAreaRef.current]);

  const handleKeyDown = React.useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // if (isCtrl(e) && ShortcutKeys.indexOf(e.keyCode) !== -1) {
    //   const formatterType: (FormatterType | undefined) = getFormatterTypeFromShortcut(e);
    //   if (formatterType) {
    //     applyMarkup(formatterType, textAreaRef.current!);
    //     e.stopPropagation();
    //     e.preventDefault();
    //   }
    // }

    props.onKeyDown?.(e);
  }, [props.onKeyDown, textAreaRef.current]);

  const handleSelect = React.useCallback((e: React.SyntheticEvent<HTMLTextAreaElement>) => {
    // setActiveFormatters(getActiveFormatters(textAreaRef.current));
    props.onSelect?.(e);
  }, [props.onSelect, textAreaRef.current]);

  const handleFocusBlur = React.useCallback<React.FocusEventHandler<HTMLElement>>(e => {
    // setFocusWithin(e.type === 'focus' || e.currentTarget.contains(findBlurRelatedTarget(e)));
  }, []);

  return (
    <div
      className={EditorStyleName}
      data-tb-test-id={makeTestId(testId!, '-TextFormatterEditorWidget')}
      onBlurCapture={handleFocusBlur}
      onFocusCapture={handleFocusBlur}
      style={style}
    >
      {label && <label htmlFor={textAreaId}>{label}</label>}
      {/* <Tooltip
        getContent={() => disabled && disabledTooltipText ? <span>{disabledTooltipText}</span> : null}
        followCursor={true}
        position={TooltipPosition.Top}
      >
        <TextFormatterToolbar
          activeFormatters={focusWithin ? activeFormatters : []}
          disabled={!!disabled}
          textAreaId={textAreaId}
          textAreaElement={textAreaRef.current}
        />
      </Tooltip> */}
      <TextAreaWidget
        {...restProps}
        className={TypeStyle.classes(className, TextAreaStyleName)}
        value={markup}
        disabled={disabled}
        id={textAreaId}
        label={null} // null because we render a label separately above, so that it appears above the toolbar
        message={message}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onSelect={handleSelect}
        testId={testId!}
        ref={textAreaRef}
        valid={valid}
      />
      <span className={MarkupDocsStyleName}>
        {!disabled && <Tooltip getContent={FormattingHintsTable} maxWidth={-1}>
          <span style={{ display: 'inline-flex', alignItems: 'center', ...PreventUserSelect }} >
            <InfoIcon />
            {Messages.formattingGuide()}
          </span>
        </Tooltip>}
      </span>
    </div>
  );
};
TextFormatterEditorWidget.displayName = 'TextFormatterEditorWidget';

// the following is an interpreted-dance reimagining of FormattingHints.tsx from vizportalclient
const FormattingHeaderStyleName = TypeStyle.style({
  backgroundColor: TabStyles.Colors.F3,
  padding: 6,
  $nest: { '& ~ *': { padding: 6 } },
});
const FormattingHintsTable = () =>
  <div style={{ display: 'inline-flex', whiteSpace: 'nowrap' }}>
    <div>
      <div className={FormattingHeaderStyleName}>{Messages.formattingHintsColumnExample()}</div>
      <div><a href='javascript:void(0)'>{Messages.formattingHintsLink()}</a></div>
      <div><strong>{Messages.formattingHintsBold()}</strong></div>
      <div><em>{Messages.formattingHintsItalics()}</em></div>
      <div><ins>{Messages.formattingHintsUnderline()}</ins></div>
      <div><img src={encodeSvg(ImageHintSvg)} style={{ width: 19, height: 19, verticalAlign: 'middle' }} />{Messages.formattingHintsImage()}</div>
    </div>
    <div>
      <div className={FormattingHeaderStyleName}>{Messages.formattingHintsColumnMarkup()}</div>
      <div>{`"${Messages.formattingHintsLink()}":http://www.example.com`}</div>
      <div>{`*${Messages.formattingHintsBold()}*`}</div>
      <div>{`_${Messages.formattingHintsItalics()}_`}</div>
      <div>{`+${Messages.formattingHintsUnderline()}+`}</div>
      <div>{'!http://www.example.com/image.png!'}</div>
    </div>
  </div>;

const ImageHintSvg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 19 19'>
  <g fill='#666'>
    <circle cx='6' cy='5' r='1.1' />
    <path d='M17 16c.5 0 1-.5 1-1V2c0-.5-.5-1-1-1H2c-.5 0-1 .5-1 1v13c0 .5.5 1 1 1h15zM2 15V2h15v13H2z' />
    <path d='M1.5 14.3l5-5-.5-.8-4.5 4.3zM13.6 7.3l-4.3 4.1-.9-.5 4.5-4.2z' />
    <path d='M9.3 11.4l-4-2 .7-.9 3.7 1.9zM17.5 9.5l-4.6-2.8-.7.8 5.3 3.2z' />
  </g>
</svg>`;
